import React, {useMemo, useCallback, useRef, useEffect, useState} from 'react';
import {Box, Button} from '@chakra-ui/react';

import CuratedEventContent from './CuratedEventContent';
import {useInfiniteGlobalSearchWithParamsAndLocation} from '../../../hooks/useFetchGlobalSearch';
import {useInfiniteMeetups} from '../../../hooks/useMeetUps';
import useGetUserLocation from '../../../hooks/useUserLocation';
import {calculateDistance, convertFootToMiles} from '../../../utils/distance';
import {filterPlaceByType} from '../../../endpoints/api';
import {SpinnerLoader} from '../../../components/Loader';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {Event} from '../../../types/article';
import useAuth from '../../../hooks/useAuth';
import {BlueButton} from '../../../components/button';
import {TextG16} from '../../../components/typography';

dayjs.extend(utc);
dayjs.extend(timezone);

interface FarmersMarketContentProps {
  eventIds?: number[];
  event?: Event;
}

const FarmersMarketContent: React.FC<FarmersMarketContentProps> = ({eventIds = [], event}) => {
  const userLocation = useGetUserLocation();
  const {user} = useAuth();

  const isActiveMember = user?.hasActiveMembership;

  // Track whether we've ever successfully loaded data to prevent showing loading spinner on refetches
  const hasEverLoadedData = useRef(false);
  // State to accumulate all loaded offers
  const [accumulatedOffers, setAccumulatedOffers] = useState<any[]>([]);
  // State to control preview of insider perks for non-members
  const [showInsiderPerksPreview, setShowInsiderPerksPreview] = useState(false);

  // Target location for North Park Farmers Market
  // 32.74736357982693, -117.13100518613525
  const targetLocation = {
    latitude: 32.74736357982693,
    longitude: -117.13100518613525,
  };

  // Environment-based topic IDs
  const npFarmersMarketTopicId = useMemo(() => {
    // @ts-ignore - process.env is defined at build time
    return process.env.NODE_ENV === 'production' ? '306' : '213';
  }, []);

  const artsCultureTopicId = useMemo(() => {
    // @ts-ignore - process.env is defined at build time
    return process.env.NODE_ENV === 'production' ? '310' : '215';
  }, []);

  const insidersClubTopicId = useMemo(() => {
    // @ts-ignore - process.env is defined at build time
    return process.env.NODE_ENV === 'production' ? '294' : '212';
  }, []);

  // Place type IDs based on actual data structure
  const placeTypeIds = useMemo(
    () => ({
      // @ts-ignore - process.env is defined at build time
      parking: process.env.NODE_ENV === 'production' ? 24 : 23,
    }),
    []
  );

  // Function to categorize places by type
  const categorizePlaceByType = useCallback(
    (place: any): 'parking' | 'places' | 'arts-culture' => {
      if (!place.place_type_id) return 'places'; // Default to 'places'

      const typeId = place.place_type_id;
      if (typeId === placeTypeIds.parking) return 'parking';

      return 'places'; // Default to 'places' for all other types
    },
    [placeTypeIds]
  );

  // Stable user location with fallback to farmers market location
  const stableUserLocation = useMemo(() => {
    if (!userLocation?.data?.latitude || !userLocation?.data?.longitude) {
      // Fall back to farmers market location if user location is not available
      return {
        latitude: targetLocation.latitude,
        longitude: targetLocation.longitude,
      };
    }
    return {
      latitude: userLocation.data.latitude,
      longitude: userLocation.data.longitude,
    };
  }, [userLocation?.data?.latitude, userLocation?.data?.longitude]);

  // Search parameters
  const offerSort = useMemo(() => ({by: 'ends_at', direction: 'asc'}), []);
  const placeSort = useMemo(() => ({by: 'default', direction: 'asc'}), []);
  const eventSort = useMemo(() => ({by: 'start_date_time', direction: 'asc'}), []);

  const offersSearchParams = useMemo(() => {
    const params = {
      type: 'offer',
      lat: targetLocation.latitude,
      long: targetLocation.longitude,
      // 'topics[]': npFarmersMarketTopicId,
      term: '',
      orderBy: 'ends_at',
      sort: offerSort,
    };
    return params;
  }, [targetLocation.latitude, targetLocation.longitude, npFarmersMarketTopicId, offerSort]);

  const eventSearchParams = useMemo(
    () => ({
      type: 'event',
      lat: targetLocation.latitude,
      long: targetLocation.longitude,
      'topics[]': npFarmersMarketTopicId, // Search for Farmers Market events only
      term: '',
      orderBy: 'starts_at',
      sort: eventSort,
    }),
    [eventSort, npFarmersMarketTopicId]
  );

  const placesSearchParams = useMemo(
    () => ({
      type: 'place',
      lat: targetLocation.latitude,
      long: targetLocation.longitude,
      'topics[]': npFarmersMarketTopicId,
      term: '',
      orderBy: 'default',
      sort: placeSort,
    }),
    [npFarmersMarketTopicId, placeSort]
  );

  // Arts & Culture search params - requires both NP farmers market AND arts & culture tags
  const artsCultureSearchParams = useMemo(
    () => ({
      type: 'place',
      lat: targetLocation.latitude,
      long: targetLocation.longitude,
      'topics[]': [npFarmersMarketTopicId, artsCultureTopicId],
      term: '',
      orderBy: 'default',
      sort: placeSort,
    }),
    [npFarmersMarketTopicId, artsCultureTopicId, placeSort]
  );

  // Data fetching hooks - only call when all required params are available
  const fetchFarmersMarketsOffers =
    useInfiniteGlobalSearchWithParamsAndLocation(offersSearchParams);
  const fetchFarmersMarketsEvents = useInfiniteGlobalSearchWithParamsAndLocation(eventSearchParams);
  const fetchFarmersMarketsPlaces =
    useInfiniteGlobalSearchWithParamsAndLocation(placesSearchParams);
  const fetchArtsCulturePlaces =
    useInfiniteGlobalSearchWithParamsAndLocation(artsCultureSearchParams);
  const fetchMeetups = useInfiniteMeetups();

  // Auto-fetch all pages for offers when the first page loads
  useEffect(() => {
    if (
      fetchFarmersMarketsOffers.data &&
      fetchFarmersMarketsOffers.hasNextPage &&
      !fetchFarmersMarketsOffers.isFetchingNextPage
    ) {
      fetchFarmersMarketsOffers.fetchNextPage();
    }
  }, [
    fetchFarmersMarketsOffers.data,
    fetchFarmersMarketsOffers.hasNextPage,
    fetchFarmersMarketsOffers.isFetchingNextPage,
    fetchFarmersMarketsOffers,
  ]);

  // Accumulate offers in state as new pages are loaded
  useEffect(() => {
    if (!fetchFarmersMarketsOffers.data?.pages) return;
    // Flatten all offers from all pages
    const allOffers = fetchFarmersMarketsOffers.data.pages.flatMap((page) => page.data.data || []);
    setAccumulatedOffers((prev) => {
      // Only update if the offers have changed (avoid unnecessary re-renders)
      if (prev.length === allOffers.length) return prev;
      return allOffers;
    });
  }, [fetchFarmersMarketsOffers.data?.pages]);

  // Check if all required parameters are valid
  const areParamsValid = useMemo(() => {
    return (
      offersSearchParams.type &&
      offersSearchParams.lat &&
      offersSearchParams.long &&
      eventSearchParams.type &&
      eventSearchParams.lat &&
      eventSearchParams.long &&
      placesSearchParams.type &&
      placesSearchParams.lat &&
      placesSearchParams.long &&
      artsCultureSearchParams.type &&
      artsCultureSearchParams.lat &&
      artsCultureSearchParams.long &&
      npFarmersMarketTopicId
    );
  }, [
    offersSearchParams,
    eventSearchParams,
    placesSearchParams,
    artsCultureSearchParams,
    npFarmersMarketTopicId,
  ]);

  // Check if all data is loaded and parameters are valid
  const isDataLoaded = useMemo(() => {
    if (!areParamsValid) return false;

    // Check if all fetch operations have completed (regardless of whether they returned data)
    const allFetchesComplete =
      !fetchFarmersMarketsOffers.isLoading &&
      !fetchFarmersMarketsOffers.isFetchingNextPage &&
      !fetchFarmersMarketsEvents.isLoading &&
      !fetchFarmersMarketsPlaces.isLoading &&
      !fetchArtsCulturePlaces.isLoading &&
      !fetchMeetups.isLoading;

    // Update the ref when all fetches are complete
    if (allFetchesComplete) {
      hasEverLoadedData.current = true;
    }

    return allFetchesComplete;
  }, [
    areParamsValid,
    fetchFarmersMarketsOffers.isLoading,
    fetchFarmersMarketsEvents.isLoading,
    fetchFarmersMarketsPlaces.isLoading,
    fetchArtsCulturePlaces.isLoading,
    fetchMeetups.isLoading,
    event?.id,
  ]);

  // Check if this is initial loading (no data yet and currently loading)
  // Only show loading spinner on true initial load, not on refetches
  const isInitialLoading = useMemo(() => {
    if (!areParamsValid) return true;

    // If we've ever loaded data before, never show loading spinner again
    if (hasEverLoadedData.current) return false;

    // Check if we have any data at all
    const hasAnyData =
      fetchFarmersMarketsOffers.data ||
      fetchFarmersMarketsEvents.data ||
      fetchFarmersMarketsPlaces.data ||
      fetchArtsCulturePlaces.data ||
      fetchMeetups.data;

    // If we already have some data, we're not in initial loading
    if (hasAnyData) {
      hasEverLoadedData.current = true;
      return false;
    }

    // Check if any hook is loading and this is the first time
    const isAnyLoading =
      fetchFarmersMarketsOffers.isLoading ||
      fetchFarmersMarketsOffers.isFetchingNextPage ||
      fetchFarmersMarketsEvents.isLoading ||
      fetchFarmersMarketsPlaces.isLoading ||
      fetchArtsCulturePlaces.isLoading ||
      fetchMeetups.isLoading;

    // Only show initial loading if we have no data AND something is loading AND we've never loaded before
    return isAnyLoading && !hasEverLoadedData.current;
  }, [
    areParamsValid,
    fetchFarmersMarketsOffers.data,
    fetchFarmersMarketsEvents.data,
    fetchFarmersMarketsPlaces.data,
    fetchArtsCulturePlaces.data,
    fetchMeetups.data,
    fetchFarmersMarketsOffers.isLoading,
    fetchFarmersMarketsEvents.isLoading,
    fetchFarmersMarketsPlaces.isLoading,
    fetchArtsCulturePlaces.isLoading,
    fetchMeetups.isLoading,
  ]);

  // Custom data loader for Farmers Market
  const customDataLoader = useMemo(() => {
    return {
      loadOffers: () => {
        // Don't process if we don't have any data yet
        if (!fetchFarmersMarketsOffers.data || !fetchFarmersMarketsOffers.data.pages) {
          return [];
        }

        // Only skip processing if we're still fetching the next page AND we haven't loaded all pages yet
        if (fetchFarmersMarketsOffers.isFetchingNextPage && fetchFarmersMarketsOffers.hasNextPage) {
          return [];
        }

        // Use accumulatedOffers from state
        const allOffers = accumulatedOffers || [];

        // If no offers from the location-based search, let's check if there are ANY offers in the system
        if (allOffers.length === 0) {
          return [];
        }

        // Categorize offers and return them with custom properties
        const categorizedOffers = allOffers
          .filter((offer) => {
            // Check if offer has valid coordinates from associated place or event
            let offerLocation: {latitude: number; longitude: number} | null = null;

            // First try to get coordinates from the associated place
            if (offer.place && offer.place.latitude && offer.place.longitude) {
              offerLocation = {
                latitude: parseFloat(String(offer.place.latitude)),
                longitude: parseFloat(String(offer.place.longitude)),
              };
            }
            // If no place coordinates, try the associated event
            else if (offer.event && offer.event.latitude && offer.event.longitude) {
              offerLocation = {
                latitude: parseFloat(String(offer.event.latitude)),
                longitude: parseFloat(String(offer.event.longitude)),
              };
            }
            // Last resort: check if offer itself has coordinates (unlikely but possible)
            else if (offer.latitude && offer.longitude) {
              offerLocation = {
                latitude: parseFloat(String(offer.latitude)),
                longitude: parseFloat(String(offer.longitude)),
              };
            }

            if (!offerLocation) {
              console.log(
                'Filtering out offer (no coordinates from place/event):',
                offer.title || offer.id
              );
              return false;
            }

            // Distance check (within 1 mile of farmers market)
            const distance = convertFootToMiles(
              calculateDistance(targetLocation, offerLocation)
            );

            if (distance > 1) {
              console.log(
                'Filtering out offer (too far):',
                offer.title || offer.id,
                `${distance.toFixed(2)} miles`
              );
              return false;
            }

            // Availability check - offer should not be expired
            if (offer.ends_at) {
              const now = dayjs();
              const endsAt = dayjs(offer.ends_at);
              if (endsAt.isBefore(now)) {
                console.log(
                  'Filtering out offer (expired):',
                  offer.title || offer.id,
                  'ended at:',
                  offer.ends_at
                );
                return false;
              }
            }

            return true;
          })
          .map((offer) => {
            // Check the communities property for community ID 16 (insider community)
            const communitiesArray = offer.communities || [];
            const hasInsiderCommunity = communitiesArray.some((community) => community.id === 16);

            // If it has the insider community, it's an insider experience
            if (hasInsiderCommunity) {
              return {
                ...offer,
                customCategory: 'offers', // Will go to "offers" section (Insider Experiences)
              };
            } else {
              // All other offers go to special offers

              return {
                ...offer,
                customCategory: 'special-offers', // Will go to "special-offers" section
              };
            }
          });

        return categorizedOffers;
      },
      loadEvents: () => {
        const allEvents =
          fetchFarmersMarketsEvents.data?.pages.flatMap((page) => page.data.data) || [];

        // Imply Farmers Market topic ID for all events since we searched with that topic
        const eventsWithImpliedTopics = allEvents.map((event) => ({
          ...event,
          topics: event.topics || [{id: npFarmersMarketTopicId, name: 'North Park Farmers Market'}],
        }));

        // Check for meetups in the events data
        const meetupsInEvents = eventsWithImpliedTopics.filter((event) => event.is_meetup === true);

        meetupsInEvents.forEach((meetup) => {
          console.log('Meetup in events:', {
            title: meetup.title,
            start_date_time: meetup.start_date_time,
            is_meetup: meetup.is_meetup,
          });
        });

        // Filter events by distance and NP farmers market tag (no date filtering for events)
        if (!event || !event.start_date_time) {
          // No reference event - filter by distance and NP tag only
          const filtered = eventsWithImpliedTopics.filter((searchEvent) => {
            if (!searchEvent.latitude || !searchEvent.longitude) {
              return false;
            }

            // For meetups, we're more lenient with topic requirements - just check distance
            const isMeetup = searchEvent.is_meetup === true;
            if (!isMeetup) {
              // Check both topics_of_interest and topics arrays (API uses topics_of_interest)
              const topicsArray = searchEvent.topics_of_interest || searchEvent.topics || [];
              const hasFarmersMarketTopic = topicsArray.some(
                (topic) => topic.id == npFarmersMarketTopicId
              );
              if (!hasFarmersMarketTopic) {
                return false;
              }
            }

            const eventLocation = {
              latitude: parseFloat(String(searchEvent.latitude)),
              longitude: parseFloat(String(searchEvent.longitude)),
            };

            const distance = convertFootToMiles(
              calculateDistance(targetLocation, eventLocation)
            );

            return distance <= 1;
          });

          return filtered;
        }

        // Filter by distance and NP farmers market tag (no same-day filtering for events)
        const referenceEventDate = dayjs.utc(event.start_date_time).local();
        const now = dayjs();

        const filtered = eventsWithImpliedTopics.filter((searchEvent) => {
          if (!searchEvent.latitude || !searchEvent.longitude) {
            return false;
          }

          // For meetups, we're more lenient with both topic and date requirements
          const isMeetup = searchEvent.is_meetup === true;
          if (!isMeetup) {
            // Check both topics_of_interest and topics arrays (API uses topics_of_interest)
            const topicsArray = searchEvent.topics_of_interest || searchEvent.topics || [];
            const hasFarmersMarketTopic = topicsArray.some(
              (topic) => topic.id == npFarmersMarketTopicId
            );
            if (!hasFarmersMarketTopic) {
              return false;
            }
          }

          // For meetups, apply same-day check - for events, skip date filtering
          if (isMeetup && searchEvent.start_date_time) {
            // Same day check (only for meetups)
            const searchEventStartDate = dayjs.utc(searchEvent.start_date_time).local();
            const searchEventEndDate = searchEvent.end_date_time
              ? dayjs.utc(searchEvent.end_date_time).local()
              : null;

            const startsOnSameDay = searchEventStartDate.isSame(referenceEventDate, 'day');
            if (!startsOnSameDay) {
              return false;
            }

            // Not ended check for meetups
            if (searchEventEndDate && searchEventEndDate.isBefore(now)) {
              return false;
            }
          } else if (!isMeetup && searchEvent.end_date_time) {
            // For events, only check if they haven't ended (no same-day requirement)
            const searchEventEndDate = dayjs.utc(searchEvent.end_date_time).local();
            if (searchEventEndDate.isBefore(now)) {
              return false;
            }
          }

          // Distance check (within 1 mile)
          const eventLocation = {
            latitude: parseFloat(String(searchEvent.latitude)),
            longitude: parseFloat(String(searchEvent.longitude)),
          };

          const distance = convertFootToMiles(
            calculateDistance(targetLocation, eventLocation)
          );
          return distance <= 1;
        });

        return filtered;
      },
      loadMeetups: () => {
        // Load meetups from the dedicated fetchMeetups hook
        const allMeetups = fetchMeetups.data?.pages.flatMap((page) => page.data.data) || [];

        // Filter meetups for farmers market specific criteria
        const filteredMeetups = allMeetups.filter((meetup) => {
          // Must have coordinates
          if (!meetup.latitude || !meetup.longitude) {
            console.log('Filtering out meetup (no coordinates):', meetup.title);
            return false;
          }

          // If we have a reference event, filter by same day
          if (event && event.start_date_time && meetup.start_date_time) {
            const referenceEventDate = dayjs.utc(event.start_date_time).local();
            const meetupStartDate = dayjs.utc(meetup.start_date_time).local();
            const now = dayjs();

            // Same day check
            const startsOnSameDay = meetupStartDate.isSame(referenceEventDate, 'day');
            if (!startsOnSameDay) {
              console.log('Filtering out meetup (different day):', meetup.title);
              return false;
            }

            // Not ended check (if meetup has an end time)
            if (meetup.end_date_time) {
              const meetupEndDate = dayjs.utc(meetup.end_date_time).local();
              if (meetupEndDate.isBefore(now)) {
                console.log('Filtering out meetup (already ended):', meetup.title);
                return false;
              }
            }
          }

          // Distance check (within 1 mile of farmers market)
          const meetupLocation = {
            latitude: parseFloat(String(meetup.latitude)),
            longitude: parseFloat(String(meetup.longitude)),
          };

          const distance = convertFootToMiles(
            calculateDistance(targetLocation, meetupLocation)
          );

          const isWithinDistance = distance <= 1;
          if (!isWithinDistance) {
            console.log('Filtering out meetup (too far):', meetup.title);
          }

          return isWithinDistance;
        });

        return filteredMeetups;
      },
      loadPlaces: async () => {
        const allPlaces =
          fetchFarmersMarketsPlaces.data?.pages.flatMap((page) => page.data.data) || [];

        // Load parking places using filterPlaceByType endpoint
        let parkingPlaces = [];
        try {
          const parkingResponse = await filterPlaceByType({
            typeIds: [String(placeTypeIds.parking)],
            lat: targetLocation.latitude,
            long: targetLocation.longitude,
            page: '1',
          });

          if (parkingResponse?.data?.data) {
            // Filter parking places to only those within 1 mile
            parkingPlaces = parkingResponse.data.data
              .filter((place) => {
                if (!place.latitude || !place.longitude) return false;

                const placeLocation = {
                  latitude: parseFloat(String(place.latitude)),
                  longitude: parseFloat(String(place.longitude)),
                };

                const distance = convertFootToMiles(
                  calculateDistance(targetLocation, placeLocation)
                );
                return distance <= 1;
              })
              .map((place) => ({
                ...place,
                category: 'parking',
              }));
          }
        } catch (error) {
          console.error('Error fetching parking places:', error);
        }

        // Load Arts & Culture places (excluding parking to prevent duplicates)
        const artsCulturePlaces =
          fetchArtsCulturePlaces.data?.pages.flatMap((page) => page.data.data) || [];

        // Filter to only include places that have BOTH the farmers market AND arts & culture topics
        const filteredArtsCulturePlaces = artsCulturePlaces.filter((place) => {
          // Check both 'topics_of_interest' and 'topics' arrays (API uses topics_of_interest)
          const topicsArray = place.topics_of_interest || place.topics || [];

          if (!Array.isArray(topicsArray)) {
            return false;
          }

          const topicIds = topicsArray.map((topic) => String(topic.id));
          const hasFarmersMarketTopic = topicIds.includes(npFarmersMarketTopicId);
          const hasArtsCultureTopic = topicIds.includes(artsCultureTopicId);

          // Must have BOTH topics
          return hasFarmersMarketTopic && hasArtsCultureTopic;
        });

        const categorizedArtsCulturePlaces = filteredArtsCulturePlaces
          .filter((place) => {
            const isNotParking = place.place_type_id !== placeTypeIds.parking;
            return isNotParking;
          }) // Exclude parking from arts & culture
          .map((place) => {
            const categorizedPlace = {
              ...place,
              category: 'arts-culture',
            };
            return categorizedPlace;
          });

        // Get IDs of places that are in the arts & culture category to exclude from regular places
        const artsCulturePlaceIds = new Set(categorizedArtsCulturePlaces.map((place) => place.id));

        // Add category information to regular places (excluding parking and arts & culture places)
        const categorizedPlaces = allPlaces
          .filter((place) => {
            const notParking = place.place_type_id !== placeTypeIds.parking;
            const notArtsCulture = !artsCulturePlaceIds.has(place.id);

            return notParking && notArtsCulture;
          }) // Exclude parking since we fetch it separately via filterPlaceByType and exclude arts & culture places
          .map((place) => ({
            ...place,
            category: categorizePlaceByType(place),
          }));

        // Deduplicate places by ID to prevent the same place appearing in multiple categories
        const allCombinedPlaces = [
          ...categorizedPlaces,
          ...parkingPlaces,
          ...categorizedArtsCulturePlaces,
        ];
        const seenPlaceIds = new Set();
        const uniquePlaces = allCombinedPlaces.filter((place) => {
          if (seenPlaceIds.has(place.id)) {
            return false; // Skip duplicate
          }
          seenPlaceIds.add(place.id);
          return true;
        });

        // Combine all places
        const finalPlaces = uniquePlaces;

        // Add the farmers market center as a special place marker
        const farmersMarketCenter = {
          id: 'farmers-market-center', // Give it a unique ID
          name: 'North Park Farmers Market',
          description: 'The heart of the farmers market experience',
          latitude: targetLocation.latitude,
          longitude: targetLocation.longitude,
          customIcon: 'FARMERS_MARKET_CENTER', // This will trigger the special marker
          category: 'places',
          // Additional properties to make it look like a proper place object
          place_type_id: null,
        };
        
        console.log('Created Farmers Market center with customIcon:', farmersMarketCenter.customIcon);

        return [farmersMarketCenter, ...finalPlaces];
      },
      loadPartners: () => {
        // Partners are filtered from places in the main component
        return [];
      },
    };
  }, [
    accumulatedOffers,
    // Force re-creation when data becomes available or content changes
    !!fetchFarmersMarketsOffers.data?.pages?.length,
    fetchFarmersMarketsOffers.data?.pages?.reduce(
      (total, page) => total + (page?.data?.data?.length || 0),
      0
    ),
    !!fetchFarmersMarketsEvents.data?.pages?.length,
    fetchFarmersMarketsEvents.data?.pages?.reduce(
      (total, page) => total + (page?.data?.data?.length || 0),
      0
    ),
    !!fetchFarmersMarketsPlaces.data?.pages?.length,
    fetchFarmersMarketsPlaces.data?.pages?.reduce(
      (total, page) => total + (page?.data?.data?.length || 0),
      0
    ),
    !!fetchArtsCulturePlaces.data?.pages?.length,
    fetchArtsCulturePlaces.data?.pages?.reduce(
      (total, page) => total + (page?.data?.data?.length || 0),
      0
    ),
    !!fetchMeetups.data?.pages?.length,
    fetchMeetups.data?.pages?.reduce((total, page) => total + (page?.data?.data?.length || 0), 0),
    // Loading states that affect when data is ready
    fetchFarmersMarketsOffers.isFetchingNextPage,
    fetchFarmersMarketsOffers.hasNextPage,
    // Other dependencies
    event,
    npFarmersMarketTopicId,
    insidersClubTopicId,
    artsCultureTopicId,
    categorizePlaceByType,
    placeTypeIds,
    // New dependencies for membership and preview state
    isActiveMember,
    showInsiderPerksPreview,
  ]);

  // Farmers Market configuration
  const farmersMarketConfig = useMemo(
    () => ({
      title: 'North Park Farmers Market',
      layout: 'map-and-list' as const,
      mapConfig: {
        enabled: true,
        title: 'Nearby Experiences Map',
        center: targetLocation,
        initialZoom: 15,
        height: '225px',
        showUserLocation: true,
      },
      sections: {
        meetups: {
          enabled: true,
          title: 'Meet-ups',
          icon: '👥',
          backgroundColor: '#faf5ff',
          textColor: '#805ad5',
          borderColor: '#d6bcfa',
          hoverColor: '#e9d8fd',
          circleColor: '#805ad5',
          description: '{count} meet-up{count !== 1 ? "s" : ""} to join',
          sortBy: 'distance' as const,
        },
        'arts-culture': {
          enabled: true,
          title: 'Arts & Culture',
          icon: '🎨',
          backgroundColor: '#fef5e7',
          textColor: '#d69e2e',
          borderColor: '#fbd38d',
          hoverColor: '#fff3cd',
          circleColor: '#d69e2e',
          description: '{count} arts & culture {count !== 1 ? "venues" : "venue"} to explore',
          sortBy: 'distance' as const,
        },
        events: {
          enabled: true,
          title: 'Events',
          icon: '📅',
          backgroundColor: '#faf5ff',
          textColor: '#805ad5',
          borderColor: '#d6bcfa',
          hoverColor: '#e9d8fd',
          circleColor: '#805ad5',
          description: '{count} event{count !== 1 ? "s" : ""} happening',
          sortBy: 'distance' as const,
        },
        parking: {
          enabled: true,
          title: 'Parking',
          icon: '🅿️',
          backgroundColor: '#f0fff4',
          textColor: '#38a169',
          borderColor: '#9ae6b4',
          hoverColor: '#c6f6d5',
          circleColor: '#38a169',
          description: '{count} parking {count !== 1 ? "areas" : "area"} nearby',
          sortBy: 'distance' as const,
        },

        partners: {
          enabled: false, // Farmers Market doesn't use partners
          title: 'Partners',
          icon: '🏢',
          backgroundColor: '#f0fff4',
          textColor: '#38a169',
          borderColor: '#9ae6b4',
          hoverColor: '#c6f6d5',
          circleColor: '#38a169',
          description: '{count} partner{count !== 1 ? "s" : ""}',
          sortBy: 'distance' as const,
        },
        places: {
          enabled: true, // Enable places section for general places (includes shopping)
          title: 'Places',
          icon: '📍',
          backgroundColor: '#f7fafc',
          textColor: '#4a5568',
          borderColor: '#cbd5e0',
          hoverColor: '#edf2f7',
          circleColor: '#4a5568',
          description: '{count} place{count !== 1 ? "s" : ""} to visit',
          sortBy: 'distance' as const,
        },
        offers: {
          enabled: true,
          title: isActiveMember ? 'Insider Experiences' : "For Local Insider's Club Members",
          icon: '⭐',
          backgroundColor: '#f0f4ff',
          textColor: '#4c51bf',
          borderColor: '#a5b4fc',
          hoverColor: '#ddd6fe',
          circleColor: '#D97706', // Warm amber/orange - more sophisticated and less bright
          description: isActiveMember
            ? '{count} exclusive insider experience{count !== 1 ? "s" : ""} available'
            : "Join the Local Insider's Club to unlock exclusive perks and VIP experiences",
          sortBy: 'distance' as const,
          customHeaderContent: !isActiveMember
            ? {
                showInsiderClubPromo: true,
                showPreviewButton: true,
                previewButtonText: 'Preview Insider Perks Nearby',
                previewExpanded: showInsiderPerksPreview,
                onPreviewToggle: () => setShowInsiderPerksPreview(!showInsiderPerksPreview),
                membershipUrl: '/memberships',
              }
            : undefined,
        },
        'special-offers': {
          enabled: true,
          title: 'Special Offers',
          icon: '🎁',
          backgroundColor: '#fff5f5',
          textColor: '#c53030',
          borderColor: '#feb2b2',
          hoverColor: '#fed7d7',
          circleColor: '#c53030',
          description: '{count} special offer{count !== 1 ? "s" : ""} available',
          sortBy: 'distance' as const,
        },
      },
      customCategorization: {
        enabled: true,
        categoryFunction: categorizePlaceByType,
        placeTypeMapping: placeTypeIds,
      },
      distanceFilter: {
        enabled: true,
        maxDistance: 1,
        centerLocation: targetLocation,
      },
      dateFilter: {
        enabled: true,
        referenceEvent: event,
        mode: 'same-day' as const,
      },
    }),
    [event, categorizePlaceByType, placeTypeIds, isActiveMember, showInsiderPerksPreview]
  );

  return (
    <>
      {isInitialLoading && (
        <div
          style={{
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <SpinnerLoader />
        </div>
      )}
      {areParamsValid && !isInitialLoading && (
        <>
          {/* Get Directions to Market Section */}
          <Box mb={4} px={3}>
            <BlueButton
              width="100%"
              leftIcon={<Box fontSize="18px">🧭</Box>}
              onClick={(e) => {
                e.preventDefault();
                const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${targetLocation.latitude},${targetLocation.longitude}`;
                window.open(directionsUrl, '_blank');
              }}
              py={6}>
              <TextG16>Get Directions to the Market</TextG16>
            </BlueButton>
          </Box>
          <CuratedEventContent
            topicId={npFarmersMarketTopicId}
            config={farmersMarketConfig}
            customDataLoader={customDataLoader}
          />
        </>
      )}
    </>
  );
};

export default FarmersMarketContent;
